import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useEffect, useMemo, useCallback, useContext } from 'react';
import { NavMarker } from 'react-rhumb';
import getLang from 'I18n/utils/getLang';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { H2 } from 'UIComponents/elements/headings';
import UIBreadcrumbs from 'UIComponents/nav/UIBreadcrumbs';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import { PUBLIC_LANGUAGES } from 'marketplace-ui-common/constants/Languages';
import MainSection from 'marketplace-ui-common/layout/components/MainSection';
import MainSectionInner from 'marketplace-ui-common/layout/components/MainSectionInner';
import CanonicalLink from 'ecosystem-marketplace-core/components/elements/CanonicalLink';
import HrefLangLink from 'marketplace-ui-common/components/elements/HrefLangLink';
import MetaDescription from 'ecosystem-marketplace-core/components/elements/MetaDescription';
import MetaOpenGraph from 'ecosystem-marketplace-core/components/elements/MetaOpenGraph';
import MetaTwitter from 'ecosystem-marketplace-core/components/elements/MetaTwitter';
import DocumentHead from 'ecosystem-marketplace-core/components/portals/DocumentHead';
import PrerenderReady from 'ecosystem-marketplace-core/components/prerender/PrerenderReady'; // @ts-expect-error untyped dependency

import { PublicOnly } from '../../selectors/auth';
import { getAppStorefrontRoute, getFeaturedAppsPublicUrl // @ts-expect-error untyped dependency
} from '../../utils/urls';
import { useSocialScreenshotUrl } from '../../utils/hooks/useSocialScreenshot';
import AppCollectionsSection from '../components/AppCollectionsSection';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
export default function AllAppCollectionsContainer() {
  const lang = getLang();
  const canonicalUrl = getFeaturedAppsPublicUrl(lang);
  const renderedHrefLangLinks = useMemo(() => {
    const hrefLangConfigs = [{
      href: getFeaturedAppsPublicUrl('en'),
      isDefault: true,
      lang: 'en'
    }, ...PUBLIC_LANGUAGES.map(_lang => ({
      href: getFeaturedAppsPublicUrl(_lang),
      isDefault: false,
      lang: _lang
    }))];
    return hrefLangConfigs.map(({
      href,
      isDefault,
      lang: _lang
    }) => /*#__PURE__*/_jsx(HrefLangLink, {
      href: href,
      lang: _lang,
      "x-default": isDefault
    }, `href-lang-${isDefault ? 'default' : _lang}`));
  }, []);
  const socialScreenshotUrl = useSocialScreenshotUrl();
  const metaDescription = unescapedText('appMarketplace.storefront.featured.metaDescription');
  const {
    trackInteraction,
    trackPageView
  } = useContext(StorefrontUsageTrackerContext);
  const onTrackInteraction = useCallback((action, eventProperties = {}) => {
    trackInteraction(action, Object.assign({}, eventProperties), 'storefront-featured-apps-interaction');
  }, [trackInteraction]);
  useEffect(() => {
    trackPageView({
      category: 'featured apps'
    });
  }, [trackPageView]);
  const trackBreadcrumbLinkClick = useCallback(value => {
    onTrackInteraction('click breadcrumb link', {
      category: 'featured apps',
      value
    });
  }, [onTrackInteraction]);
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "FEATURED_APPS_LOAD",
    children: [/*#__PURE__*/_jsx(PublicOnly, {
      children: /*#__PURE__*/_jsx(DocumentHead, {
        children: /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(MetaDescription, {
            content: metaDescription
          }), /*#__PURE__*/_jsx(MetaTwitter, {
            name: "card"
          }), /*#__PURE__*/_jsx(MetaTwitter, {
            name: "site"
          }), /*#__PURE__*/_jsx(MetaTwitter, {
            name: "image",
            content: socialScreenshotUrl
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "type"
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "url",
            content: canonicalUrl
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "description",
            content: metaDescription
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "image",
            content: socialScreenshotUrl
          }), /*#__PURE__*/_jsx(CanonicalLink, {
            href: canonicalUrl
          }), renderedHrefLangLinks, /*#__PURE__*/_jsx(PrerenderReady, {})]
        })
      })
    }), /*#__PURE__*/_jsxs("main", {
      children: [/*#__PURE__*/_jsx(MainSection, {
        className: "p-y-0",
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsxs(UIBreadcrumbs, {
            flush: true,
            children: [/*#__PURE__*/_jsx(UIRouterLink, {
              onClick: () => trackBreadcrumbLinkClick('app storefront'),
              to: getAppStorefrontRoute(),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "appMarketplace.storefront.featured.breadCrumbs.root"
              })
            }), /*#__PURE__*/_jsx("span", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "appMarketplace.storefront.featured.breadCrumbs.featuredApps"
              })
            })]
          })
        })
      }), /*#__PURE__*/_jsx(MainSection, {
        className: "p-bottom-0",
        children: /*#__PURE__*/_jsxs(MainSectionInner, {
          children: [/*#__PURE__*/_jsx(H2, {
            "aria-level": 2,
            children: /*#__PURE__*/_jsx("strong", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "appMarketplace.storefront.featured.title"
              })
            })
          }), /*#__PURE__*/_jsx("p", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "appMarketplace.storefront.featured.subtitle"
            })
          })]
        })
      }), /*#__PURE__*/_jsx(MainSection, {
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsx(AppCollectionsSection, {
            trackInteraction: onTrackInteraction
          })
        })
      })]
    })]
  });
}